import { useEffect, useMemo, useState } from 'react'
import { ResolutionFeedback } from '../components/ResolutionFeedback'
import { HighlightsBanner } from '../components/HighlightsBanner'
import { DATASETS, DATASET_MAP } from '../lib/datasets'
import { loadDataset } from '../lib/data'
import type { DatasetKey } from '../types'
import { OPERATIONAL_TOOLS, OperationalTool, type ToolKey } from './operational/OperationalTools'
import { TarifaEconomiasTool } from './operational/TarifaEconomiasTool'
import { DynamicNegotiationTool } from './operational/DynamicNegotiationTool'
import { DevolucaoValoresCC } from './operational/DevolucaoValoresCC'
import { EmpreendimentosCobranca } from './operational/EmpreendimentosCobranca'
import { QualificacoesClassifier } from './operational/QualificacoesClassifier'
import { AcionamentosCentral } from './operational/AcionamentosCentral'
import { SuiteHelpOperational } from './operational/SuiteHelpOperational'
import { TabelaServicosOperational } from './operational/TabelaServicosOperational'
import { IrregularidadesOperational } from './operational/IrregularidadesOperational'
import { TitularidadeReligacaoFlow } from './operational/TitularidadeReligacaoFlow'
import { ImplantacaoAguaEsgotoFlow } from './operational/ImplantacaoAguaEsgotoFlow'
import { VencimentoGrupo } from './operational/VencimentoGrupo'
import { NovaLimaMatriculas } from './operational/NovaLimaMatriculas'
import { FiqueEmDiaConsulta } from './operational/FiqueEmDiaConsulta'
import type { ParcelamentoCampanha } from '../lib/parcelamentoCampanha'
import { GlobalSearch, type GlobalSearchDestination } from './operational/GlobalSearch'

type OperationalView = GlobalSearchDestination | 'vencimento-grupo' | 'nova-lima-matriculas' | 'fique-em-dia'

type PendingPrefill = {
  destination: DatasetKey
  value: string
}

const TOOL_KEYS = new Set<ToolKey>(OPERATIONAL_TOOLS.map((item) => item.key))
const DATASET_KEYS = new Set<DatasetKey>(DATASETS.map((item) => item.key))

const WORK_LABELS: Partial<Record<OperationalView, string>> = {
  desconto: 'Simulador de Desconto',
  retro: 'Cálculo Retroativo',
  tarifa: 'Valor por m³',
  parcel: 'Simulador de Parcelamento',
  negocia: 'Negociação de Dívidas',
  'vencimento-grupo': 'Vencimento por Grupo',
  'nova-lima-matriculas': 'Nova Lima e Região - Matrículas',
  'fique-em-dia': 'Fique em Dia 2026: Consulta por CPF/CNPJ',
  'devolucao-cc': 'Devolução de Valores CC',
  'titularidade-religa': 'Troca de Titularidade / Religação a Pedido',
  'implantacao-agua-esgoto': 'Implantação de Água / Esgoto',
  empreendimentos: 'Empreendimentos',
  suite_help: 'Suite Help',
  qualificacoes: 'Qualificações',
  acionamentos: 'Acionamentos',
  servicos: 'Tabela de Serviços',
  irregularidades: 'Irregularidades',
}

function isTool(value: OperationalView): value is ToolKey {
  return TOOL_KEYS.has(value as ToolKey)
}

function isDataset(value: OperationalView): value is DatasetKey {
  return DATASET_KEYS.has(value as DatasetKey)
}

function displayValue(value: unknown) {
  if (value == null || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function Workspace() {
  const [active, setActive] = useState<OperationalView>('inicio')
  const [records, setRecords] = useState<Record<string, unknown>[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [source, setSource] = useState<'online' | 'cache'>('online')
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [pendingPrefill, setPendingPrefill] = useState<PendingPrefill | null>(null)
  const [parcelamentoCampanha, setParcelamentoCampanha] = useState<ParcelamentoCampanha | null>(null)
  const [proceduresOpen, setProceduresOpen] = useState(false)

  useEffect(() => {
    if (!isDataset(active)) {
      setLoading(false)
      setError('')
      return
    }

    let mounted = true
    setLoading(true)
    setError('')
    loadDataset(active)
      .then((result) => {
        if (!mounted) return
        setRecords(result.records)
        setSource(result.source)
        setSavedAt(result.savedAt)
      })
      .catch((err) => {
        if (!mounted) return
        setError(err instanceof Error ? err.message : 'Base indisponível.')
        setRecords([])
      })
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [active])

  useEffect(() => {
    if (!pendingPrefill || pendingPrefill.destination !== active || !isDataset(active)) return

    const timer = window.setTimeout(() => {
      const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('.workspace-content input[type="search"]'))
      const target = inputs.find((input) => !input.closest('.global-search'))
      if (!target) return

      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
      setter?.call(target, pendingPrefill.value)
      target.dispatchEvent(new Event('input', { bubbles: true }))
      target.focus()
      setPendingPrefill(null)
    }, 80)

    return () => window.clearTimeout(timer)
  }, [active, pendingPrefill])

  const config = isDataset(active) ? DATASET_MAP[active] : null
  const normalized = query.trim().toLocaleLowerCase('pt-BR')

  const filtered = useMemo(() => {
    if (!config) return []
    if (!normalized) return records
    return records.filter((record) =>
      config.searchFields.some((field) =>
        displayValue(record[field]).toLocaleLowerCase('pt-BR').includes(normalized),
      ),
    )
  }, [records, config, normalized])

  function navigate(next: OperationalView, prefill = '') {
    // Módulo temporariamente inativo: impedir acesso por qualquer atalho interno.
    if (next === 'devolucao-cc') return
    if (next !== 'parcel') setParcelamentoCampanha(null)
    setActive(next)
    setQuery('')
    setProceduresOpen(false)
    setPendingPrefill(prefill && isDataset(next) ? { destination: next, value: prefill } : null)
  }

  function abrirParcelamentoCampanha(dados: ParcelamentoCampanha) {
    setParcelamentoCampanha(dados)
    navigate('parcel')
  }

  const localView = active === 'inicio' || active === 'vencimento-grupo' || active === 'devolucao-cc' || active === 'titularidade-religa' || active === 'implantacao-agua-esgoto' || isTool(active)
  const isHome = active === 'inicio'

  function renderActiveContent() {
    return (
      <>
        {isTool(active) && (active === 'tarifa' ? <TarifaEconomiasTool /> : active === 'negocia' ? <DynamicNegotiationTool /> : <OperationalTool tool={active}
          parcelamentoCampanha={active === 'parcel' ? parcelamentoCampanha : null}
          onVoltarCampanha={() => navigate('fique-em-dia')} />)}
        {active === 'vencimento-grupo' && <VencimentoGrupo />}
        {active === 'nova-lima-matriculas' && <NovaLimaMatriculas />}
        {(active === 'fique-em-dia' || (active === 'parcel' && parcelamentoCampanha !== null)) && (
          <div style={{ display: active === 'fique-em-dia' ? 'contents' : 'none' }}>
            <FiqueEmDiaConsulta onSimularParcelamento={abrirParcelamentoCampanha} />
          </div>
        )}
        {active === 'devolucao-cc' && <DevolucaoValoresCC />}
        {active === 'titularidade-religa' && <TitularidadeReligacaoFlow />}
        {active === 'implantacao-agua-esgoto' && <ImplantacaoAguaEsgotoFlow />}
        {active === 'empreendimentos' && <EmpreendimentosCobranca records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}
        {active === 'qualificacoes' && <QualificacoesClassifier records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}
        {active === 'acionamentos' && <AcionamentosCentral records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}
        {active === 'suite_help' && <SuiteHelpOperational records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}
        {active === 'servicos' && <TabelaServicosOperational records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}
        {active === 'irregularidades' && <IrregularidadesOperational records={records} source={source} savedAt={savedAt} loading={loading} error={error} />}

        {config && !['empreendimentos', 'qualificacoes', 'acionamentos', 'suite_help', 'servicos', 'irregularidades'].includes(active) && (
          <>
            <div className="dataset-toolbar">
              <div><h2>{config.label}</h2><p className="muted">{records.length} registros carregados{source === 'cache' && savedAt ? ` · cache ${new Date(savedAt).toLocaleString('pt-BR')}` : ''}</p></div>
              <label className="search-field"><span className="visually-hidden">Pesquisar em {config.label}</span><input type="search" placeholder={`Pesquisar em ${config.label}...`} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
            </div>
            {loading && <div className="empty-state">Carregando base...</div>}
            {error && <div className="notice error" role="alert">{error}</div>}
            {!loading && !error && (
              <div className="record-grid">
                {filtered.slice(0, 150).map((record, index) => (
                  <article className="record-card" key={String(record.id ?? `${active}-${index}`)}>
                    <div className="record-title">{config.titleFields.map((field) => <strong key={field}>{displayValue(record[field])}</strong>)}</div>
                    <dl>{config.detailFields.map((field) => <div key={field}><dt>{field.replaceAll('_', ' ')}</dt><dd>{displayValue(record[field])}</dd></div>)}</dl>
                  </article>
                ))}
                {filtered.length === 0 && <div className="empty-state">Nenhum resultado encontrado.</div>}
              </div>
            )}
          </>
        )}
      </>
    )
  }

  if (!isHome) {
    const feedbackLabel = WORK_LABELS[active] ?? config?.label ?? 'Conteúdo do Suite'
    return (
      <section className="workspace-content operation-workspace-mode">
        <div className="operation-workbar">
          <div className="operation-breadcrumb"><span>Atendimento</span><b>/</b><strong>{feedbackLabel}</strong></div>
          <span className={`status-chip ${!localView && source === 'cache' ? 'warning' : ''}`}>
            {localView ? 'Ferramenta local' : source === 'cache' ? 'Contingência offline' : 'Dados online'}
          </span>
        </div>
        <div className="operation-work-content">
          {renderActiveContent()}
          <div className="operation-return-row">
            <button type="button" className="operation-back-button" onClick={() => navigate('inicio')}>← Retornar ao painel de Atendimento</button>
          </div>
          <ResolutionFeedback targetKey={String(active)} targetLabel={feedbackLabel} />
        </div>
      </section>
    )
  }

  return (
    <section className="workspace-content operation-dashboard">
      <div className="page-heading operation-dashboard-heading">
        <div>
          <p className="eyebrow">AMBIENTE OPERACIONAL</p>
          <h1>Atendimento</h1>
          <p className="muted">Cálculos, consultas e bases oficiais do atendimento AGB.</p>
        </div>
        <div className="status-chip">Dados online</div>
      </div>

      <HighlightsBanner />

      <section className="operation-home-hero operation-home-hero--intro">
        <p className="eyebrow">SUITE DE PROCEDIMENTOS</p>
        <h2>Ferramentas e consultas em um único ambiente</h2>
        <p>Abra uma ferramenta ou consulta para entrar no modo de trabalho. O painel é recolhido automaticamente para manter o foco no atendimento.</p>
      </section>

      <GlobalSearch onNavigate={navigate} />

      <section className="operation-nav-section">
        <div className="operation-nav-section__head"><h2>Atalhos</h2><span>Ferramentas operacionais preservadas e atualizadas no Suite</span></div>
        <div className="operation-shortcuts">
          {OPERATIONAL_TOOLS.map((tool) => (
            <button key={tool.key} className="operation-shortcut" onClick={() => navigate(tool.key)}>
              <span className="operation-shortcut__icon">{tool.icon}</span><span><strong>{tool.label}</strong><small>{tool.description}</small></span>
            </button>
          ))}
          <button className="operation-shortcut" onClick={() => navigate('vencimento-grupo')}>
            <span className="operation-shortcut__icon">◫</span><span><strong>Vencimento por Grupo</strong><small>Consulta os dias permitidos pelo Grupo de Leitura em calendário.</small></span>
          </button>
          <button className="operation-shortcut" onClick={() => navigate('fique-em-dia')}>
            <span className="operation-shortcut__icon">⌕</span><span><strong>Fique em Dia 2026: Consulta por CPF/CNPJ</strong><small>Consulta individual de elegibilidade e etapas da campanha.</small></span>
          </button>
          <button className="operation-shortcut" onClick={() => navigate('nova-lima-matriculas')}>
            <span className="operation-shortcut__icon">⌕</span><span><strong>Nova Lima e Região - Matrículas</strong><small>Consulta matrículas da base vigente para cobrança de terra na contratação do esgoto.</small></span>
          </button>
          <button className={`operation-shortcut procedure-menu-button ${proceduresOpen ? 'active' : ''}`} onClick={() => setProceduresOpen((value) => !value)} aria-expanded={proceduresOpen}>
            <span className="operation-shortcut__icon">◇</span><span><strong>Procedimentos Guiados</strong><small>Flows operacionais por etapas, documentos, rede, TIL e OS.</small></span><span className="procedure-menu-chevron">{proceduresOpen ? '−' : '+'}</span>
          </button>
        </div>

        {proceduresOpen && (
          <div className="procedure-menu-panel">
            <div className="procedure-menu-head"><div><p className="eyebrow">PROCEDIMENTOS GUIADOS</p><strong>Escolha o flow que deseja iniciar</strong></div><button type="button" onClick={() => setProceduresOpen(false)}>Fechar</button></div>
            <div className="procedure-menu-grid">
              <button type="button" onClick={() => navigate('titularidade-religa')}><span>⇄</span><div><strong>Troca de Titularidade / Religação a Pedido</strong><small>Validação documental por vínculo e pendências.</small></div><b>→</b></button>
              <button type="button" onClick={() => navigate('implantacao-agua-esgoto')}><span>⌁</span><div><strong>Implantação de Água / Esgoto</strong><small>Rede, extensão, pavimento, ramal/TIL e OS.</small></div><b>→</b></button>
            </div>
          </div>
        )}
      </section>

      <section className="operation-nav-section">
        <div className="operation-nav-section__head"><h2>Consultas oficiais</h2><span>6 bases sincronizadas e disponíveis em contingência</span></div>
        <nav className="dataset-tabs operation-dataset-tabs" aria-label="Bases do Suite">
          {DATASETS.map((dataset) => (
            <button key={dataset.key} onClick={() => navigate(dataset.key)}>
              <strong>{dataset.label}</strong><span>{dataset.description}</span>
            </button>
          ))}
        </nav>
      </section>
    </section>
  )
}
